import { FilterGroup, FilterCondition, FilterConfig, FieldOption } from '@aidkitorg/types/lib/admin-panel/filters';
import { BooleanExpr } from '@aidkitorg/types/lib/survey';

/**
 * Converts FilterConfig (or FilterGroup) to BooleanExpr format for backend
 * This is used when saving filters (reports, applicant groups, dashboards, etc.)
 */
export class FilterGroupToBooleanExprConverter {

  /**
   * Converts a FilterConfig to a BooleanExpr
   */
  static convertFromConfig(filterConfig: FilterConfig): BooleanExpr | undefined {
    if (!filterConfig.enabled || !filterConfig.config) {
      return undefined;
    }

    return FilterGroupToBooleanExprConverter.convert(filterConfig.config);
  }

  /**
   * Converts a filter group to a BooleanExpr
   */
  static convert(group: FilterGroup): BooleanExpr {
    const clauses: BooleanExpr[] = [];

    for (const condition of group.conditions) {
      if ('field' in condition) {
        // It's a FilterCondition
        const booleanExpr = FilterGroupToBooleanExprConverter.convertCondition(condition);
        if (booleanExpr) {
          clauses.push(booleanExpr);
        }
      } else {
        // It's a nested FilterGroup
        clauses.push(FilterGroupToBooleanExprConverter.convert(condition));
      }
    }

    if (clauses.length === 0) {
      // Return a "true" expression that doesn't filter anything
      return {
        kind: 'Not',
        clause: { kind: 'Never' }
      };
    }

    if (clauses.length === 1) {
      return clauses[0];
    }

    // Return appropriate logic combination
    return group.logic === 'and'
      ? { kind: 'And', clauses }
      : { kind: 'Or', clauses };
  }

  /**
   * Converts a single filter condition to a BooleanExpr
   */
  private static convertCondition(condition: FilterCondition): BooleanExpr | null {
    const { field, operator, value } = condition;

    switch (operator) {
      case 'equals':
        if (value == null) return null;
        return {
          kind: 'Value Equals',
          value: { kind: 'Field', field },
          equals: { kind: 'StringValue', value }
        };

      case 'not_equals':
        if (value == null) return null;
        return {
          kind: 'Value Not Equal',
          value: { kind: 'Field', field },
          notEqual: { kind: 'StringValue', value }
        };

      case 'contains':
        if (value == null) return null;
        return {
          kind: 'Value Contains',
          mode: 'string',
          caseInsensitive: true,
          value: { kind: 'Field', field },
          contains: { kind: 'StringValue', value }
        };

      case 'not_contains':
        if (value == null) return null;
        return {
          kind: 'Not',
          clause: {
            kind: 'Value Contains',
            mode: 'string',
            caseInsensitive: true,
            value: { kind: 'Field', field },
            contains: { kind: 'StringValue', value }
          }
        };

      case 'empty':
        return {
          kind: 'Value Empty',
          value: { kind: 'Field', field }
        };

      case 'not_empty':
        return {
          kind: 'Value Not Empty',
          value: { kind: 'Field', field }
        };

      case 'greater_than':
        if (value == null) return null;
        return {
          kind: 'Value Greater Than',
          value: { kind: 'Field', field },
          greaterThan: { kind: 'NumericValue', value: Number(value) }
        };

      case 'less_than':
        if (value == null) return null;
        return {
          kind: 'Value Less Than',
          value: { kind: 'Field', field },
          lessThan: { kind: 'NumericValue', value: Number(value) }
        };

      case 'after':
        if (value == null || value.trim() === '') return null;
        return {
          kind: 'Value Greater Than',
          value: { kind: 'As Date', value: { kind: 'Field', field } },
          greaterThan: { kind: 'As Date', value: { kind: 'StringValue', value } }
        };

      case 'before':
        if (value == null || value.trim() === '') return null;
        return {
          kind: 'Value Less Than',
          value: { kind: 'As Date', value: { kind: 'Field', field } },
          lessThan: { kind: 'As Date', value: { kind: 'StringValue', value } }
        };

      case 'exists':
        return {
          kind: 'Exists',
          field
        };

      case 'not_exists':
        return {
          kind: 'DoesntExist',
          field
        };

      default:
        console.warn(`Unknown filter operator: ${operator}`);
        return null;
    }
  }
}

/**
 * Reverse converter - converts BooleanExpr back to FilterGroup format
 * This is used when initializing FilterPanel with existing filters from the backend
 */
export class BooleanExprToFilterGroupConverter {

  /**
   * Converts a BooleanExpr to FilterGroup for FilterPanel initialization
   */
  static convert(booleanExpr: BooleanExpr | any, availableFields?: FieldOption[]): FilterGroup | undefined {
    if (!booleanExpr || typeof booleanExpr !== 'object') return undefined;

    const { kind } = booleanExpr;

    // Handle And/Or operations
    if (kind === 'And' || kind === 'Or') {
      const logic = kind.toLowerCase() as 'and' | 'or';
      const conditions: Array<FilterCondition | FilterGroup> = [];

      if (booleanExpr.clauses && Array.isArray(booleanExpr.clauses)) {
        for (const clause of booleanExpr.clauses) {
          // For clauses, try to convert to condition first, then fallback to group
          const condition = BooleanExprToFilterGroupConverter.convertToCondition(clause, availableFields);
          if (condition) {
            conditions.push(condition);
          } else {
            // If it's not a simple condition, recursively convert as group
            const group = BooleanExprToFilterGroupConverter.convert(clause, availableFields);
            if (group) {
              conditions.push(group);
            }
          }
        }
      }

      return conditions.length > 0 ? { logic, conditions } : undefined;
    }

    // Handle simple field operations at the top level - wrap in a group
    const condition = BooleanExprToFilterGroupConverter.convertToCondition(booleanExpr, availableFields);
    if (condition) {
      return {
        logic: 'and',
        conditions: [condition]
      };
    }

    return undefined;
  }

  /**
   * Converts a single BooleanExpr to FilterCondition
   */
  private static convertToCondition(booleanExpr: any, availableFields?: FieldOption[]): FilterCondition | undefined {
    if (!booleanExpr || typeof booleanExpr !== 'object') return undefined;

    const { kind } = booleanExpr;

    // Handle field-based expressions first
    if (booleanExpr.field) {
      const condition: FilterCondition = {
        field: booleanExpr.field,
        operator: 'equals', // default
        value: undefined
      };

      switch (kind) {
        case 'Exists':
          condition.operator = 'exists';
          break;
        case 'DoesntExist':
          condition.operator = 'not_exists';
          break;
        case 'Equals':
          condition.operator = 'equals';
          condition.value = booleanExpr.value;
          break;
        case 'Not Equal':
          condition.operator = 'not_equals';
          condition.value = booleanExpr.value;
          break;
        default:
          return undefined;
      }

      return condition;
    }

    // Handle value-based expressions
    let fieldName: string | undefined;

    // Check for direct field access
    if (booleanExpr.value && booleanExpr.value.kind === 'Field') {
      fieldName = booleanExpr.value.field;
    }
    // Check for field wrapped in As Date
    else if (booleanExpr.value && booleanExpr.value.kind === 'As Date' && booleanExpr.value.value && booleanExpr.value.value.kind === 'Field') {
      fieldName = booleanExpr.value.value.field;
    }

    if (fieldName) {

      const condition: FilterCondition = {
        field: fieldName,
        operator: 'equals', // default
        value: undefined
      };

      switch (kind) {
        case 'Value Equals':
          condition.operator = 'equals';
          condition.value = booleanExpr.equals?.value;
          break;
        case 'Value Not Equal':
          condition.operator = 'not_equals';
          condition.value = booleanExpr.notEqual?.value;
          break;
        case 'Value Contains':
          condition.operator = 'contains';
          condition.value = booleanExpr.contains?.value;
          break;
        case 'Value Empty':
          condition.operator = 'empty';
          break;
        case 'Value Not Empty':
          condition.operator = 'not_empty';
          break;
        case 'Value Greater Than':
          // Check if this is a date comparison (As Date wrapper) or numeric comparison
          if (booleanExpr.greaterThan?.kind === 'As Date') {
            condition.operator = 'after';
            condition.value = booleanExpr.greaterThan?.value?.value;
          } else {
            condition.operator = 'greater_than';
            condition.value = booleanExpr.greaterThan?.value;
          }
          break;
        case 'Value Less Than':
          // Check if this is a date comparison (As Date wrapper) or numeric comparison
          if (booleanExpr.lessThan?.kind === 'As Date') {
            condition.operator = 'before';
            condition.value = booleanExpr.lessThan?.value?.value;
          } else {
            condition.operator = 'less_than';
            condition.value = booleanExpr.lessThan?.value;
          }
          break;
        default:
          return undefined;
      }

      return condition;
    }

    // Handle Not expressions (for not_contains)
    if (kind === 'Not' && booleanExpr.clause) {
      const innerExpr = booleanExpr.clause;
      if (innerExpr.kind === 'Value Contains' && innerExpr.value?.kind === 'Field') {
        const fieldName = innerExpr.value.field;
        if (fieldName) {
          return {
            field: fieldName,
            operator: 'not_contains',
            value: innerExpr.contains?.value
          };
        }
      }
    }

    return undefined;
  }
}
